import dayjs from 'dayjs';
import { getStoredSizes } from '@/lib/utils';
import { createPortal } from 'react-dom';
import { AlertCircle, Locate, Mic, StopCircle, X } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import AIIcon from '@/assets/pngs/ai_icon.svg';
import {
  Button,
  Combobox,
  Input,
  Label,
  LocationInput,
  MultiSelectDropdown,
  Switch,
  Textarea,
  useToast,
} from '@/components';
import { useERSContext, useMapContext } from '@/context';
import { useAIProvider } from '@/hooks/useAIProvider';
import {
  capitalizeWords,
  constants,
  DESCRIPTION_VALIDATION_PROMPT,
  fetchRouteAndUpdateResponder,
  getAddressFromCoord,
  getCurrentCoord,
  INCIDENT_EXTRACTION_PROMPT,
  INPUT_LIMITS,
  isValidCoordinates,
  mapbox_accessToken,
  parseGPSInput,
  SUBMIT_DEBOUNCE_MS,
  sanitizeInput,
  severityValues,
  updateRespondersDistance,
  validationRules,
} from '@/lib';
import { ResponderDetail } from '.';
import { VoiceVisualizer } from './VoiceVisualizer';

function getCurrentDateTime() {
  return dayjs().format('MM/DD/YYYY HH:mm');
}

const asterisk = "after:content-['*'] after:text-red-500 after:ml-1";

const statusValues = [
  {
    value: 'open',
    label: 'Created',
  },
  {
    value: 'in_progress',
    label: 'Progress',
    disabled: false,
  },
];

export const CreateIncident = ({ handleFormCancel, responders }) => {
  const { selfProfileRef, incidentTypes, incidentAssigneeStatusList } = useERSContext();
  const { t } = useTranslation();
  const {
    plotDraggableIncidentMarker,
    createIncidentMarker,
    endCreateIncidentFlow,
    setRespondersForCreateIncident,
    setEditingIncidentAndCandidates,
  } = useMapContext();

  const [respondersWithDistance, setRespondersWithDistance] = useState(responders);
  const { showToast } = useToast();
  const { callAPI, callTranscribeAudio } = useAIProvider({ temperature: 0.9 });

  const [selectedValues, setSelectedValues] = useState([]);
  const selectedUsers = respondersWithDistance?.filter(opt => selectedValues.includes(opt.id));

  //Code for allow drag and move the incident creation window
  const popupRef = useRef(null);
  const offset = useRef({ x: 0, y: 0 });
  const isDragging = useRef(false);
  const onMouseDown = e => {
    if (!popupRef.current) return;
    isDragging.current = true;
    offset.current = {
      x: e.clientX - popupRef.current.offsetLeft,
      y: e.clientY - popupRef.current.offsetTop,
    };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  };
  const onMouseMove = e => {
    if (!isDragging.current || !popupRef.current) return;
    popupRef.current.style.left = `${e.clientX - offset.current.x}px`;
    popupRef.current.style.top = `${e.clientY - offset.current.y}px`;
  };
  const onMouseUp = () => {
    isDragging.current = false;
    document.removeEventListener('mousemove', onMouseMove);
    document.removeEventListener('mouseup', onMouseUp);
  };
  const [initOffset] = useState(() => ({
    x: Math.abs(Math.floor(Math.random() * 30)) + 20,
    y: Math.floor(Math.random() * 30),
  }));

  const typingTimeoutRef = useRef(null);

  const getInitialIncident = () => ({
    id: uuidv4(),
    organizationId: selfProfileRef?.current?.organizationId,
    selectedIncidentType: null,
    selectedSeverity: null,
    selectedStatus: statusValues[0],
    description: '',
    geoloc: { lat: null, long: null },
    location: '',
    location_description: '',
    assignees: [],
    creationTimestamp: dayjs().unix(),
    created_by: {
      display_name: selfProfileRef?.current?.displayName || 'NA',
      user_id: selfProfileRef.current.id,
    },
    lastUpdateTimestamp: dayjs().unix(),
    updated_by: {
      display_name: selfProfileRef?.current?.displayName || 'NA',
      user_id: selfProfileRef.current.id,
    },
    communicationType: 7,
  });

  const [formData, setFormData] = useState(getInitialIncident);

  const [errors, setErrors] = useState({
    description: '',
    location: '',
    location_description: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [incidentTypesOptions] = useState(
    incidentTypes?.map(item => {
      return {
        value: item?.display_name,
        label:
          t('INCIDENT_TYPES', { returnObjects: true }).find(type => type.value === item?.display_name)?.label ||
          item?.display_name,
      };
    }) || []
  );

  // location input states
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // ai features states
  const [isDescriptionValid, setIsDescriptionValid] = useState(false);
  const [aiEnabled, setEnabled] = useState(false);
  const [aiValidationError, setAiValidationError] = useState('');
  const [isValidatingDescription, setIsValidatingDescription] = useState(false);
  const [isGeneratingFormData, setIsGeneratingFormData] = useState(false);

  // transcript features
  const [recording, setRecording] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [error, setError] = useState(null);
  const [stream, setStream] = useState(null);

  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);

  const lastSubmitTime = useRef(0);
  const [lastResponderLocations, setLastResponderLocations] = useState({});

  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!formData.location || formData.location.length < 2) {
      setSuggestions([]);
      return;
    }

    const gps = parseGPSInput(formData.location);

    const fetchLocations = async () => {
      setIsLoading(true);
      try {
        if (gps) {
          // Reverse geocode the GPS coordinates to get an address
          const address = await getAddressFromCoord(gps.lat, gps.lng, mapbox_accessToken);
          setSuggestions([
            {
              id: 'gps-input',
              text: `${gps.lat}, ${gps.lng}`,
              place_name: address || `GPS: ${gps.lat}, ${gps.lng}`,
              center: [gps.lng, gps.lat],
              place_type: ['coordinate'],
            },
          ]);
        } else {
          const res = await fetch(
            `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(
              formData.location
            )}.json?access_token=${mapbox_accessToken}&limit=5`
          );
          const data = await res.json();
          setSuggestions(data.features || []);
        }
      } catch {
        setSuggestions([]);
      } finally {
        setIsLoading(false);
      }
    };

    const timeout = setTimeout(fetchLocations, 300);
    return () => clearTimeout(timeout);
  }, [formData.location]);

  // IMPROVEMENT: Reset AI validation state when AI is disabled
  useEffect(() => {
    if (!aiEnabled) {
      setIsDescriptionValid(false);
      setIsValidatingDescription(false);
      setAiValidationError('');
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    }
  }, [aiEnabled]);

  const handleResponderHover = async (options, isHovering) => {
    if (isHovering) {
      const updatedResponder = await fetchRouteAndUpdateResponder({
        responder: options,
        incidentLocation: formData,
        isSelected: false,
        isHover: true,
      });
      setRespondersForCreateIncident(prev => {
        const exists = prev.find(r => r.id === options?.id);
        if (exists) {
          return prev.map(r => (r.id === options.id ? { ...r, isHover: true } : r));
        }
        return [...prev, updatedResponder];
      });
    } else {
      setRespondersForCreateIncident(prev => prev.filter(r => !(r.id === options?.id && r.isHover && !r.isSelected)));
    }
  };

  const errorMsg = field => <p className="text-error-strong-active text-xs">{errors[field]}</p>;

  const validateField = useCallback((field, value) => {
    if (!validationRules[field]) return '';

    // Security: Enforce length limits (guard non-string values)
    if (INPUT_LIMITS[field] && typeof value === 'string' && value.length >= INPUT_LIMITS[field]) {
      return `Maximum ${INPUT_LIMITS[field]} characters allowed`;
    }

    if (!value) return '';

    const isValid = validationRules[field].regex.test(value);
    return isValid ? '' : validationRules[field].message;
  }, []);

  const isCompleteDisabled =
    !formData.description ||
    !formData.location ||
    !formData.selectedIncidentType?.value ||
    !formData.selectedSeverity?.value ||
    !!errors.description ||
    !!errors.location ||
    isSubmitting;

  const fetchLocationWithOneRecord = async query => {
    try {
      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(
          query
        )}.json?access_token=${mapbox_accessToken}&limit=1`
      );
      const data = await response.json();

      if (data.features && data.features.length > 0) {
        const place = data?.features[0];
        return {
          display_name: place.place_name, // Proper formatted name
          lat: place.center[1],
          lon: place.center[0],
          context: place.context || [],
        };
      } else {
        return null;
      }
    } catch (err) {
      console.error('Mapbox lookup failed:', err);
      return null;
    }
  };

  const checkAIdescriptionIsValid = useCallback(
    async value => {
      if (!value?.trim()) {
        setIsDescriptionValid(false);
        setAiValidationError('');
        return;
      }

      setIsValidatingDescription(true);
      setAiValidationError('');

      try {
        const messages = [
          { role: 'system', content: DESCRIPTION_VALIDATION_PROMPT },
          { role: 'user', content: value },
        ];
        const output = await callAPI(messages);
        const cleaned = output.trim().toLowerCase();
        const isValid = cleaned.includes('true');

        setIsDescriptionValid(isValid);
        if (isValid) {
          handleAISummaryFormFill();
        }

        // NEW: Set error message if description is invalid
        if (!isValid) {
          setAiValidationError('Please provide a proper incident description to use AI auto-fill feature.');
        }
      } catch (error) {
        console.error('AI validation failed:', error);
        setIsDescriptionValid(false);
        setAiValidationError('AI validation unavailable. Please try again.');
      } finally {
        setIsValidatingDescription(false);
      }
    },
    [callAPI]
  );

  const handleAISummaryFormFill = async () => {
    try {
      setIsGeneratingFormData(true);
      const messages = [
        { role: 'system', content: INCIDENT_EXTRACTION_PROMPT },
        { role: 'user', content: transcript },
      ];
      const output = await callAPI(messages);

      const cleaned = output
        .replace(/```json/g, '')
        .replace(/```/g, '')
        .trim();

      const aiData = JSON.parse(cleaned);
      console.log('content: ', aiData);

      const mapboxData = aiData?.location?.display_name
        ? await fetchLocationWithOneRecord(aiData.location.display_name)
        : null;

      const updatedLocation = mapboxData ? mapboxData : aiData.location;

      plotDraggableIncidentMarker([updatedLocation.lon, updatedLocation.lat]);

      const updatedData = {
        selectedIncidentType:
          incidentTypesOptions?.find(item => item.value?.toLowerCase() === aiData?.type?.toLowerCase()) || null,
        description: aiData?.description || '',
        selectedSeverity: severityValues?.find(item => item.value === aiData?.severity) || null,
        selectedStatus:
          statusValues?.find(item => item.value?.toLowerCase() === aiData?.status?.toLowerCase()) || statusValues[0],
        location: updatedLocation?.display_name || '',
        geoloc: { lat: updatedLocation.lat, long: updatedLocation.lon },
        location_description: aiData?.location?.location_description || '',
      };
      console.log('updated data: ', updatedData);

      setFormData(prev => ({
        ...prev,
        ...updatedData,
      }));

      showToast({
        type: 'success',
        title: 'AI Auto-fill Complete',
        description: 'Form fields populated successfully',
      });
    } catch (err) {
      console.error('Failed to parse AI output:', err);
      showToast({
        type: constants?.MESSAGE?.ERROR,
        title: 'AI Auto-fill Failed',
        description: 'Could not extract information. Please fill manually.',
      });
    } finally {
      setIsGeneratingFormData(false);
    }
  };

  const handleChange = useCallback(
    (field, value) => {
      // Security: Sanitize input
      const sanitizedValue = typeof value === 'string' ? sanitizeInput(value) : value;

      setFormData(prev => ({
        ...prev,
        [field]: sanitizedValue,
      }));

      // Validate
      const errorMessage = validateField(field, sanitizedValue);
      setErrors(prev => ({
        ...prev,
        [field]: errorMessage,
      }));
    },
    [validateField, aiEnabled, checkAIdescriptionIsValid]
  );

  const handleCancel = () => {
    setEditingIncidentAndCandidates(null);
    setFormData({
      id: uuidv4(),
      organizationId: selfProfileRef?.current?.organizationId,
      selectedIncidentType: null,
      selectedSeverity: null,
      selectedStatus: null,
      description: '',
      geoloc: { lat: null, long: null },
      location: '',
      location_description: '', // Changed from location_details
      creationTimestamp: dayjs().unix(),
      created_by: {
        display_name: selfProfileRef?.current?.displayName || 'NA',
        user_id: selfProfileRef?.current?.id || '',
      },
      updated_by: {
        display_name: selfProfileRef?.current?.displayName || 'NA',
        user_id: selfProfileRef?.current?.id || '',
      },
      lastUpdateTimestamp: dayjs().unix(),
      communicationType: 7,
    });
    handleFormCancel();
    endCreateIncidentFlow();
    setRespondersForCreateIncident([]);
  };

  // Security & Performance: Rate-limited submit with comprehensive error handling
  const handleComplete = useCallback(async () => {
    setEditingIncidentAndCandidates(null);
    // Performance: Rate limiting
    const now = Date.now();
    if (now - lastSubmitTime.current < SUBMIT_DEBOUNCE_MS) {
      setSubmitError('Please wait before submitting again');
      return;
    }
    lastSubmitTime.current = now;

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      // Security: Validate SDK availability
      if (!window.wbSdk?.incidentManager?.createIncident) {
        throw new Error('Incident creation service is unavailable. Please try again later.');
      }

      // Security: Final validation before submit
      if (!formData.description?.trim() || !formData.location?.trim()) {
        throw new Error('Please fill in all required fields');
      }

      const assignees = selectedUsers.map(item => ({
        id: item?.id.split('@')[0] || '',
        response_status: 'pending',
        status_records: [],
        display_name: item.displayName,
        user_type_id: item.userTypeId,
        team_id: null,
        is_manager: false,
      }));

      // Build location object with proper nesting
      let locationPayload;
      if (typeof formData.location === 'string') {
        // If location is a string, create object with display_name
        locationPayload = {
          display_name: sanitizeInput(formData.location),
        };
      } else {
        // If location is already an object, use it as base
        locationPayload = { ...formData.location };
      }

      // Add location_description if provided (nested inside location object)
      if (formData.location_description?.trim()) {
        locationPayload.location_description = sanitizeInput(formData.location_description);
      }

      // Security: Sanitize all string inputs in payload
      const payload = {
        id: formData.id,
        type: formData.selectedIncidentType?.value || null,
        description: sanitizeInput(formData.description),
        assignees,
        severity: formData.selectedSeverity?.value ?? null,
        priority: formData.selectedSeverity?.value ?? null,
        splashMessage: {},
        status: formData.selectedStatus?.value,
        geoloc: formData.geoloc,
        location: locationPayload, // Use the properly structured location object
        creationTimestamp: formData.creationTimestamp,
        lastUpdateTimestamp: formData.lastUpdateTimestamp,
        created_by: formData.created_by,
        updatedBy: formData.updated_by,
        reporter_info: formData.created_by?.display_name || 'Unknown',
        organizationId: formData.organizationId || null,
        orgId: formData.organizationId || null,
        communicationType: formData.communicationType || 7,
        startTimestamp: formData.creationTimestamp,
        resolvedTimestamp: -1,
      };

      const incidentCreated = await window.wbSdk.incidentManager.createIncident(payload);
      showToast({
        type: constants?.MESSAGE?.INCIDENT,
        title: incidentCreated?.title,
        description: `${constants.DASHBOARD.NEW_INCIDENT_CREATED_BY} ${incidentCreated?.created_by?.display_name} `,
        createdData: incidentCreated,
      });
      handleFormCancel();
      endCreateIncidentFlow();
      setRespondersForCreateIncident([]);
    } catch (error) {
      console.error('Failed to update incident:', error);
      setSubmitError(error.message || 'Failed to update incident. Please check your connection and try again.');
      showToast({ type: constants?.MESSAGE?.ERROR, title: constants?.MESSAGE?.INCIDENT_CREATION_ERROR });
    } finally {
      setIsSubmitting(false);
    }
  }, [formData, selectedValues, respondersWithDistance, handleFormCancel]);

  const handleSelectLocation = async (data, isInput = false) => {
    try {
      let lat, long, address;

      // Determine initial coordinates
      if (isInput && data?.coordinates) {
        lat = data.coordinates.latitude;
        long = data.coordinates.longitude;
        address = data.display_name || data.placeName;
      } else {
        const coord = await getCurrentCoord();
        lat = coord.lat;
        long = coord.long;
        address = await getAddressFromCoord(lat, long, mapbox_accessToken);
      }

      // Set initial location in form
      setFormData(prev => ({
        ...prev,
        geoloc: { lat, long },
        location: address,
      }));

      const updatedResponderWithDistance = await updateRespondersDistance(
        responders,
        { lat, long },
        mapbox_accessToken
      );
      setRespondersWithDistance(updatedResponderWithDistance);
      console.log('Map window closed');
    } catch (err) {
      console.error('Error selecting location:', err);
    }
  };

  useEffect(() => {
    const fetchAndSetAddress = async () => {
      if (!createIncidentMarker?.coord || createIncidentMarker.coord.length !== 2) return;
      try {
        const [long, lat] = createIncidentMarker.coord;

        const address = await getAddressFromCoord(lat, long, mapbox_accessToken);

        setFormData(prev => ({
          ...prev,
          geoloc: { lat, long },
          location: address,
        }));

        const updatedResponderWithDistance = await updateRespondersDistance(
          responders,
          { lat, long },
          mapbox_accessToken
        );
        setRespondersWithDistance(updatedResponderWithDistance);
      } catch (err) {
        console.error('Error fetching address:', err);
      }
    };

    fetchAndSetAddress();
  }, [createIncidentMarker]);

  useEffect(() => {
    const updateResponderDistances = async () => {
      try {
        if (!responders?.length) return;

        const coords = createIncidentMarker?.coord?.length
          ? { lat: createIncidentMarker.coord[1], long: createIncidentMarker.coord[0] }
          : formData?.geoloc;

        if (!coords?.lat || !coords?.long) return;

        let movedResponders = [];
        let anyResponderMoved = false;

        for (const responder of responders) {
          if (!responder.lastKnownLocation) continue;
          const responderCoords = [responder.lastKnownLocation.lon, responder.lastKnownLocation.lat];
          if (!isValidCoordinates(responderCoords)) continue;

          const lastCoords = lastResponderLocations[responder.id];
          const hasMoved = !lastCoords || lastCoords[0] !== responderCoords[0] || lastCoords[1] !== responderCoords[1];

          if (hasMoved) {
            anyResponderMoved = true;
            movedResponders.push(responder);

            // update stored coordinates
            setLastResponderLocations(prev => ({
              ...prev,
              [responder.id]: responderCoords,
            }));
          }
        }

        // No movement? Skip API
        if (!anyResponderMoved) {
          return;
        }

        // Update only moved responders' distances
        const updatedMovedResponders = await updateRespondersDistance(movedResponders, coords, mapbox_accessToken);

        // Merge with existing distances (keep old ones)
        setRespondersWithDistance(prev => {
          const prevMap = new Map(prev.map(r => [r.id, r]));
          for (const updated of updatedMovedResponders) {
            prevMap.set(updated.id, updated); // replace moved one with updated
          }
          return Array.from(prevMap.values());
        });
      } catch (err) {
        console.error('Error updating responder distances:', err);
      }
    };

    updateResponderDistances();
  }, [responders, createIncidentMarker?.coord, formData?.geoloc?.lat, formData?.geoloc?.long]);

  const handleAIDescriptionChange = value => {
    const sanitizedValue = typeof value === 'string' ? sanitizeInput(value) : value;
    setTranscript(value);
    if (aiEnabled) {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }

      if (sanitizedValue) {
        typingTimeoutRef.current = setTimeout(async () => {
          await checkAIdescriptionIsValid(sanitizedValue);
        }, 5000); // wait 5s after user stops typing
        // if (isDescriptionValid) {
        //   handleAISummaryFormFill();
        // }
      }
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      setStream(stream);
      const mediaRecorder = new MediaRecorder(stream);
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = e => {
        if (e.data.size > 0) {
          audioChunksRef.current.push(e.data);
        }
      };

      mediaRecorder.onstop = async () => {
        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        await transcribeAudio(blob);
      };

      mediaRecorder.start();
      mediaRecorderRef.current = mediaRecorder;
      setRecording(true);
      setError(null);
    } catch (err) {
      console.error(err);
      setError('Microphone access denied or unsupported browser');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
      setRecording(false);
    }
    setStream(null);
  };

  const transcribeAudio = async audioBlob => {
    try {
      const file = new File([audioBlob], 'speech.webm', { type: 'audio/webm' });

      const text = await callTranscribeAudio(file, {
        model: 'whisper-1',
        prompt: ` 
        You are a transcriber specialist.
        First, analyze the audio to determine the spoken language accurately.
        Then, transcribe the speech in its original language, maintaining correct grammar, punctuation, and formatting.
        Keep all proper nouns and technical terms spelled correctly.
        Do not translate - only transcribe.`,
      });

      handleAIDescriptionChange(transcript + text);
    } catch (err) {
      console.error(err);
      setError('Transcription failed: ' + err.message);
    }
  };

  const renderContent = (
    <div
      ref={popupRef}
      onMouseDown={() => {
        if (popupRef.current) {
          popupRef.current.style.zIndex = Date.now() % 86400000;
        }
      }}
      className="fixed bg rounded-xl shadow-lg text-sm flex flex-col border border-border min-w-[340px] h-3/4 overflow-hidden"
      style={{
        zIndex: 100,
        left: `${initOffset.x}px`,
        top: `${initOffset.y}px`,
        position: 'fixed',
        width: getStoredSizes()?.leftSidebarPan?.value * (getStoredSizes()?.leftSidebarPan?.incidentPan / 100) + '%',
      }}
    >
      {/* Header */}
      <div
        className="flex items-center justify-between bg-subtle border-b border-border px-3 py-2 cursor-move"
        onMouseDown={onMouseDown}
      >
        <div className="flex items-center gap-2">
          <img src={AIIcon} alt="Create Incident" className="w-5 h-5" />
          <span className="font-medium text-sm">{t('INCIDENTS_PANEL.CREATE_INCIDENT')}</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="flex items-center gap-2" title="Enable AI Assistant">
            <Switch
              checked={aiEnabled}
              onCheckedChange={value => {
                setEnabled(value);
                if (value && transcript) {
                  checkAIdescriptionIsValid(transcript);
                }
                if (!value) {
                  setFormData(getInitialIncident);
                  setTranscript('');
                }
              }}
              id="ai-assistance"
            />
            <Label htmlFor="ai-assistance" className="text-sm">
              {t('INCIDENTS_PANEL.AI_ASSISTANCE')}
            </Label>
          </span>
          <button
            className="p-1 hover:bg-strong rounded transition-colors"
            disabled={isSubmitting}
            onClick={handleCancel}
            aria-label="Close"
          >
            <X size={18} className="text-muted" />
          </button>
        </div>
      </div>

      {/* Global Error Display */}
      {submitError && (
        <div
          className="mx-3 mt-3 p-3 bg-error-subtle border border-error-strong rounded-lg flex items-start gap-2"
          role="alert"
        >
          <AlertCircle size={16} className="text-error-strong mt-0.5 flex-shrink-0" />
          <p className="text-sm text-error-strong">{submitError}</p>
        </div>
      )}

      {/* Body */}
      <div className="flex flex-col flex-1 gap-3 p-3 overflow-y-auto">
        {aiEnabled && (
          <div className="flex flex-1 flex-col gap-y-1">
            <div className="relative">
              <Textarea
                rows={6}
                placeholder={t('PLACEHOLDER.AI_DESCRIPTION')}
                className={'pb-6 gradient-border'}
                value={transcript}
                onChange={e => handleAIDescriptionChange(e.target.value)}
                maxLength={INPUT_LIMITS.description}
              />
              {isValidatingDescription && (
                <div className="absolute left-3 bottom-2 flex items-center">
                  <span className="text-xs text-muted">{t('PLACEHOLDER.VALIDATING')}</span>
                </div>
              )}
              {!isValidatingDescription && isGeneratingFormData && (
                <div className="absolute left-3 bottom-2 flex items-center">
                  <span className="text-xs text-muted">{t('PLACEHOLDER.GENERATING')}</span>
                </div>
              )}

              {
                <Button
                  variant="ghostIcon"
                  onMouseDown={startRecording}
                  onMouseUp={stopRecording}
                  onMouseLeave={stopRecording}
                  onTouchStart={startRecording}
                  onTouchEnd={stopRecording}
                  className="absolute right-0 bottom-0 flex items-center"
                >
                  {recording ? (
                    <div className="flex flex-row gap-1 items-center">
                      <VoiceVisualizer stream={stream} />
                      <StopCircle size={13} />
                    </div>
                  ) : (
                    <Mic size={13} />
                  )}
                </Button>
              }
            </div>
          </div>
        )}
        {aiEnabled && errors.description && errorMsg('description')}
        {aiEnabled && aiValidationError && !errors.description && (
          <p className="text-error-strong-active text-xs flex items-start gap-1 mt-1">
            <AlertCircle size={14} className="flex-shrink-0" />
            <span>{aiValidationError}</span>
          </p>
        )}
        <div className="flex flex-row gap-4">
          <div className="flex flex-1 flex-col gap-y-1">
            <Label>{t('INCIDENTS_PANEL.REPORTED_DATE_TIME')}</Label>
            <span className="text-muted">{getCurrentDateTime()}</span>
          </div>
          <div className="flex flex-1 flex-col gap-y-1">
            <Label>{t('INCIDENTS_PANEL.REPORTED_NAME')}</Label>
            <span className="text-muted">{selfProfileRef?.current?.displayName || 'NA'}</span>
          </div>
        </div>
        <div className="flex flex-row gap-4">
          <div className="flex flex-1 flex-col gap-y-1">
            <Label className={asterisk}>{t('INCIDENTS_PANEL.INCIDENT_TYPE')}</Label>
            <Combobox
              data={incidentTypesOptions}
              selectedData={formData.selectedIncidentType}
              onChange={selectedIncidentType => setFormData(prev => ({ ...prev, selectedIncidentType }))}
              placeholder={t('PLACEHOLDER.SELECT_OPTION')}
            />
          </div>
        </div>
        <div className="flex flex-row">
          <div className="flex flex-1 flex-col gap-y-1">
            <Label className={asterisk}>{capitalizeWords(t('INCIDENTS_PANEL.LOCATION'))}</Label>
            <div className="flex flex-row gap-x-2 align-middle">
              <LocationInput
                value={formData.location}
                onInputChange={location => setFormData(prev => ({ ...prev, location }))}
                onChange={data => handleSelectLocation(data, true)}
                isLoading={isLoading}
                suggestions={suggestions}
                placeholder={t('PLACEHOLDER.LOCATION')}
                error={null}
                handleSelectedClear={() => {
                  setFormData(prev => ({ ...prev, location: '', geoloc: { lat: null, long: null } }));
                }}
              />
              <Button
                size="sm"
                variant="outline"
                onClick={async () => {
                  const coord = await getCurrentCoord();
                  if (formData.geoloc?.long && formData.geoloc?.lat) {
                    plotDraggableIncidentMarker([formData.geoloc?.long, formData.geoloc?.lat]);
                  } else {
                    plotDraggableIncidentMarker([coord.long, coord.lat]);
                  }
                }}
              >
                <Locate size={13} />
              </Button>
            </div>
            {errors.location && errorMsg('location')}
          </div>
        </div>
        <div className="flex flex-row">
          <div className="flex flex-1 flex-col gap-y-1">
            <Label>{capitalizeWords(t('INCIDENTS_PANEL.LOCATION_DESCRIPTION'))}</Label>
            <div className="flex flex-row align-middle">
              <Input
                value={formData.location_description}
                placeholder={t('PLACEHOLDER.LOCATION_DESCRIPTION')}
                onChange={e => handleChange('location_description', e.target.value)}
              />
            </div>
            {errors.location_description && errorMsg('location_description')}
          </div>
        </div>
        <div className="flex flex-row gap-4">
          <div className="flex flex-1 flex-col gap-y-1">
            <Label className={asterisk}>{capitalizeWords(t('INCIDENTS_PANEL.SEVERITY'))}</Label>
            <Combobox
              data={t('SEVERITY_VALUES', { returnObjects: true })}
              selectedData={formData.selectedSeverity}
              onChange={selectedSeverity => setFormData(prev => ({ ...prev, selectedSeverity }))}
              placeholder={t('PLACEHOLDER.SELECT_OPTION')}
            />
          </div>
          <div className="flex flex-1 flex-col gap-y-1">
            <Label className={asterisk}>{capitalizeWords(t('INCIDENTS_PANEL.STATUS'))}</Label>
            <Combobox
              data={t('CREATE_INCIDENT_STATUS_VALUES', { returnObjects: true })}
              selectedData={formData.selectedStatus}
              onChange={selectedStatus => setFormData(prev => ({ ...prev, selectedStatus }))}
              placeholder={t('PLACEHOLDER.SELECT_OPTION')}
            />
          </div>
        </div>
        <div className="flex flex-col gap-y-2">
          <div className="flex flex-1 flex-col gap-y-1">
            <Label>{capitalizeWords(t('INCIDENTS_PANEL.RESPONDERS'))}</Label>
            <div className="rounded-lg shadow-sm">
              <MultiSelectDropdown
                expandDirection="up"
                options={respondersWithDistance}
                value={selectedValues}
                placeholder={t('INCIDENTS_PANEL.SELECT_RESPONDERS')}
                onChange={newSelectedIds => {
                  setEditingIncidentAndCandidates({
                    incident: formData,
                    candidates: newSelectedIds?.map(aID => ({ id: aID })) || [],
                  });
                  setSelectedValues(newSelectedIds);
                }}
                handleResponderHover={handleResponderHover}
              />
            </div>
          </div>
          {selectedUsers.length > 0 && (
            <div className="grid gap-2 grid-cols-[repeat(2,minmax(0,1fr))] text-muted">
              {selectedUsers?.map(item => (
                <ResponderDetail key={item.id} assignee={item} statusList={incidentAssigneeStatusList} mode="view" />
              ))}
            </div>
          )}
        </div>
        <div className="flex flex-col gap-y-2 flex-1">
          <Label className={`${asterisk} flex`}>{capitalizeWords(constants.INCIDENTS_PANEL.DESCRIPTION)}</Label>
          <div className="relative flex-1 flex">
            <Textarea
              placeholder={t('PLACEHOLDER.DESCRIPTION')}
              className="pb-6 flex-1"
              value={formData.description}
              onChange={e => handleChange('description', e.target.value)}
              maxLength={INPUT_LIMITS.description}
            />
          </div>
        </div>
      </div>
      {/* Footer */}
      <div className="flex items-center justify-end gap-2 px-3 py-2 border-t border-border">
        <Button
          aria-label="Close incident editor"
          disabled={isSubmitting}
          onClick={handleCancel}
          size="sm"
          variant="outline"
        >
          {t('INCIDENTS_PANEL.CANCEL')}
        </Button>
        <Button onClick={handleComplete} disabled={isCompleteDisabled} size="sm" variant="default">
          {t('INCIDENTS_PANEL.COMPLETE')}
        </Button>
      </div>
    </div>
  );

  return createPortal(renderContent, document.body);
};
